import React, { useState, useEffect } from "react";
import i18n from "i18next";
import { fetchWrapper } from "../../helpers/fetchwrapper";

import { MmInch } from "../../helpers/mminch";
import { helperMethods } from "../../helpers/helperMethods";

import { baseUrl } from "../../helpers/constants";
import useWindowSize from "../../helpers/useWindowSize";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import {
  Dropdown,
  MessageBar,
  Field,
  Input,
  Label,
  Image,
  Checkbox,
  Option,
} from "@fluentui/react-components";
import { ILuebOption } from "../../Models/ILuebOption";
import { ISchraubprofil } from "../../Models/ISchraubprofil";
import MeasurementInputs, { IMeasurements } from "./MeasurementInputs";

interface IGearedOffsetHeadSpecificationsProps {
  configuration: any;
  antriebsProfil: any;
  schraubfall: string;
  gearbox: { key: string; text: string };
  onChangeGearedOffsetHeadSpecifications: any;
}

function GearedOffsetHeadSpecifications(
  props: IGearedOffsetHeadSpecificationsProps,
) {
  const [abtriebsprofile, setAbtriebsprofile] = useState<ILuebOption[]>([]);

  const [specification, setSpecification] = useState({
    maxRadius: { mm: 0.0, inchDez: 0.0, inch: "" },
    maxKlingenstaerke: { mm: 0.0, inchDez: 0.0, inch: "" },
    maxBreite: { mm: 0.0, inchDez: 0.0, inch: "" },
    achsabstandMin: { mm: 0.0, inchDez: 0.0, inch: "" },
    achsabstandMax: { mm: 0.0, inchDez: 0.0, inch: "" },
    auskraglaenge: { mm: 0.0, inchDez: 0.0, inch: "" },
    kopfmass: { mm: 0.0, inchDez: 0.0, inch: "" },
    durchmesserDMax: { mm: 0.0, inchDez: 0.0, inch: "" },
    laengeLmax: { mm: 0.0, inchDez: 0.0, inch: "" },
    laengeLmin: { mm: 0.0, inchDez: 0.0, inch: "" },
    nussBit: false,
    withProtectiveCoating: false,
    abtriebsProfil: { key: "", kurzwort: "", text: "" },
  });
  const handleSpecificationValuesChange = (name: string, data: any) => {
    setSpecification((prevState) => ({
      ...prevState,
      [name]: data,
    }));
  };

  useEffect(() => {
    if (props.configuration) {
      setSpecification(props.configuration);
    }
  }, []);

  const [imageName, setImageName] = useState("");
  const [nutImageName, setNutImageName] = useState("");

  const windowSize = useWindowSize();

  let imagePreFix = "";

  if (props.gearbox.key == "Standard_FL_G") {
    imagePreFix = "";
  } else if (props.gearbox.key == "Standard_FL_O") {
    imagePreFix = "_OFFEN";
  } else if (props.gearbox.key == "Inline_FL_G") {
    imagePreFix = "_INLINE";
  } else if (props.gearbox.key == "Inline_FL_O") {
    imagePreFix = "_INLINE_OFFEN";
  }

  useEffect(() => {
    props.onChangeGearedOffsetHeadSpecifications(specification);
  }, [specification, abtriebsprofile]);

  useEffect(() => {
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/configurator/GetGegenprofile?profil=${props.antriebsProfil.kurzwort}`,
      )
      .then((data: any) => {
        let options: ILuebOption[] = [];
        data.forEach((element: any) => {
          let option: ILuebOption = {
            data: element,
            key: element.schraubprofilGuid,
            text: i18n.language.includes("de")
              ? element.displayNameDE
              : element.displayNameEN,
          };
          options.push(option);
        });
        setAbtriebsprofile(options);
        if (options.length > 0) {
          handleSpecificationValuesChange("abtriebsProfil", {
            key: options[0].key.toString(),
            kurzwort: options[0].data.kurzwort.toString(),
            text: options[0].text.toString(),
          });
        }
      });
  }, props.antriebsProfil);

  const onChangeAbtriebsprofile = (data: ISchraubprofil) => {
    handleSpecificationValuesChange("abtriebsProfil", {
      key: data ? data.schraubprofilGuid : "",
      kurzwort: data ? data.kurzwort : "",
      text: data
        ? i18n.language.includes("de")
          ? data.displayNameDE
          : data.displayNameEN
        : "",
    });
  };

  const getValFromArray = (array: any, field: string) => {
    let test = undefined;
    for (var i = 0, iLen = Object.entries(array).length; i < iLen; i++) {
      // do stuff with node.childNodes[i];
      if (Object.entries(array)[i][0] == field) {
        test = Object.entries(array)[i][1];
      }
    }
    return test;
  };

  const inchConverter = (field: string) => {
    const fieldValue = getValFromArray(specification, field);
    const inchDezValue = getValFromArray(fieldValue, "inchDez");
    const asNumber = helperMethods.convertToNumber(inchDezValue);
    const mm = helperMethods.convertToNumber(MmInch.toMM(asNumber));
    handleSpecificationValuesChange(field, {
      mm: mm,
      inchDez: asNumber,
      inch: asNumber == 0 ? "" : MmInch.toInch(mm),
    });
  };

  const fractionalConverter = (field: string) => {
    const fieldValue = getValFromArray(specification, field);
    const inchValue = getValFromArray(fieldValue, "inch");
    const mm = helperMethods.convertToNumber(MmInch.toMM(inchValue));
    handleSpecificationValuesChange(field, {
      mm: mm,
      inchDez: mm / 25.4,
      inch: inchValue,
    });
  };

  const mmConverter = (field: string) => {
    const fieldValue = getValFromArray(specification, field);
    const mmValue = getValFromArray(fieldValue, "mm");
    const mm = helperMethods.convertToNumber(mmValue as string);
    handleSpecificationValuesChange(field, {
      mm: mm,
      inchDez: mm / 25.4,
      inch: mm ? MmInch.toInch(mm) : "",
    });
  };

  return (
    <div className="lueb-MarginTop10">
      <div style={{ display: "flex", alignItems: "center" }}>
        <div>
          <MeasurementInputs
            title={"maxRadius"}
            tabIndex={1}
            currentMeasurements={specification.maxRadius}
            onChangeMeasurements={(measurements: IMeasurements) => {
              setImageName("");
              handleSpecificationValuesChange("maxRadius", measurements);
            }}
            onFocus={() => {
              setImageName("MASS_R");
            }}
          />
          <MeasurementInputs
            title={"maxKlingenstaerke"}
            tabIndex={2}
            currentMeasurements={specification.maxKlingenstaerke}
            onChangeMeasurements={(measurements: IMeasurements) => {
              setImageName("");
              handleSpecificationValuesChange(
                "maxKlingenstaerke",
                measurements,
              );
            }}
            onFocus={() => {
              setImageName("MASS_H");
            }}
          />

          <MeasurementInputs
            title={"maxBreite"}
            tabIndex={3}
            currentMeasurements={specification.maxBreite}
            onChangeMeasurements={(measurements: IMeasurements) => {
              setImageName("");
              handleSpecificationValuesChange("maxBreite", measurements);
            }}
            onFocus={() => {
              setImageName("MASS_B");
            }}
          />

          <MeasurementInputs
            title={"minAchsabstand"}
            tabIndex={4}
            currentMeasurements={specification.achsabstandMin}
            onChangeMeasurements={(measurements: IMeasurements) => {
              setImageName("");
              handleSpecificationValuesChange("achsabstandMin", measurements);
            }}
            onFocus={() => {
              setImageName("MASS_L1");
            }}
          />

          <MeasurementInputs
            title={"maxAchsabstand"}
            tabIndex={5}
            currentMeasurements={specification.achsabstandMax}
            onChangeMeasurements={(measurements: IMeasurements) => {
              setImageName("");
              handleSpecificationValuesChange("achsabstandMax", measurements);
            }}
            onFocus={() => {
              setImageName("MASS_L1");
            }}
          />
          {specification.achsabstandMax.mm > 0 &&
            specification.achsabstandMin.mm >
              specification.achsabstandMax.mm && (
              <MessageBar className="lueb-MarginTop10" intent="error">
                {i18n.t<string>("achsabstandMinError")}
              </MessageBar>
            )}

          <MeasurementInputs
            title={"minauskraglaengeL2"}
            tabIndex={6}
            currentMeasurements={specification.auskraglaenge}
            onChangeMeasurements={(measurements: IMeasurements) => {
              setImageName("");
              handleSpecificationValuesChange("auskraglaenge", measurements);
            }}
            onFocus={() => {
              setImageName("MASS_L2");
            }}
          />

          <MeasurementInputs
            title={"maxKopfmass"}
            tabIndex={7}
            readonly={
              !["Inline_FL_O", "Standard_FL_O"].includes(props.gearbox.key)
            }
            currentMeasurements={specification.kopfmass}
            onChangeMeasurements={(measurements: IMeasurements) => {
              setImageName("");
              handleSpecificationValuesChange("kopfmass", measurements);
            }}
            onFocus={() => {
              setImageName("MASS_E");
            }}
            appearance={
              !["Inline_FL_O", "Standard_FL_O"].includes(props.gearbox.key)
                ? "filled-darker"
                : undefined
            }
          />
        </div>
        {windowSize.width > 500 && imageName != "" && (
          <Image
            width={300}
            height={300}
            src={`${baseUrl}/IMAGES/CONFIST/MASS/${
              imageName + imagePreFix
            }.PNG`}
          />
        )}
        {windowSize.width > 500 && imageName == "" && (
          <div
            style={{
              background: "white",
              width: 364,
              height: 300,
            }}
          ></div>
        )}
      </div>

      <div>
        {props.schraubfall != "holdndrive" &&
          props.schraubfall != "rohrleitung" && (
            <Checkbox
              style={{ marginTop: "8px" }}
              label={i18n.t<string>("verschraubungNussBit")}
              checked={specification.nussBit}
              onChange={(ev, data) => {
                handleSpecificationValuesChange("nussBit", data.checked);
              }}
            />
          )}

        {specification.nussBit && (
          <>
            <h5 style={{ marginTop: "8px" }}>
              {props.antriebsProfil.data.isMaleProfil
                ? i18n.t<string>("nutSpecifications")
                : i18n.t<string>("bitSpecifications")}
            </h5>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div>
                {props.antriebsProfil.data.isMaleProfil && (
                  <MeasurementInputs
                    title={"durchmesserDmax"}
                    tabIndex={8}
                    currentMeasurements={specification.durchmesserDMax}
                    onChangeMeasurements={(measurements: IMeasurements) => {
                      setNutImageName("");
                      handleSpecificationValuesChange(
                        "durchmesserDMax",
                        measurements,
                      );
                    }}
                    onFocus={() => {
                      setNutImageName("MASS_NUSS_D");
                    }}
                  />
                )}
                <MeasurementInputs
                  title={"laengeLmax"}
                  tabIndex={9}
                  currentMeasurements={specification.laengeLmax}
                  onChangeMeasurements={(measurements: IMeasurements) => {
                    setNutImageName("");
                    handleSpecificationValuesChange("laengeLmax", measurements);
                  }}
                  onFocus={() => {
                    setNutImageName("MASS_NUSS_L");
                  }}
                />

                <MeasurementInputs
                  title={"laengeLmin"}
                  tabIndex={10}
                  currentMeasurements={specification.laengeLmin}
                  onChangeMeasurements={(measurements: IMeasurements) => {
                    setNutImageName("");
                    handleSpecificationValuesChange("laengeLmin", measurements);
                  }}
                  onFocus={() => {
                    setNutImageName("MASS_NUSS_L");
                  }}
                />

                <Label style={{ marginTop: "8px" }}>
                  {i18n.t<string>("abtriebsprofil")}
                </Label>
                <div style={{ width: "35vw" }}>
                  <StackShim
                    horizontal
                    tokens={{ childrenGap: 10 }}
                    wrap={true}
                  >
                    <StackItemShim
                      grow={1}
                      style={{ paddingLeft: 5, paddingRight: 5 }}
                    >
                      <Field style={{ maxWidth: "80%" }}>
                        <Dropdown
                          selectedOptions={[specification.abtriebsProfil.key]}
                          defaultSelectedOptions={[
                            specification.abtriebsProfil.key,
                          ]}
                          onOptionSelect={(ev, item) => {
                            if (
                              abtriebsprofile &&
                              item.optionValue != undefined
                            ) {
                              var abtriebsprofil = abtriebsprofile.find(
                                (x) => x.key == item.optionValue,
                              )?.data;
                            }
                            onChangeAbtriebsprofile(abtriebsprofil);
                          }}
                          value={
                            abtriebsprofile.find(
                              (x) => x.key == specification.abtriebsProfil.key,
                            )?.text
                          }
                          defaultValue={
                            abtriebsprofile.find(
                              (x) => x.key == specification.abtriebsProfil.key,
                            )?.text
                          }
                        >
                          {abtriebsprofile.map((item) => {
                            if (typeof item.key == "string")
                              return (
                                <Option value={item.key}>{item.text}</Option>
                              );
                          })}
                        </Dropdown>
                      </Field>
                    </StackItemShim>
                    <StackItemShim
                      grow={1}
                      style={{ paddingLeft: 5, paddingRight: 5 }}
                    >
                      {specification.abtriebsProfil.kurzwort && (
                        <Image
                          height="80px"
                          src={`${baseUrl}/IMAGES/PROFILBILDER/SOCKET_${specification.abtriebsProfil.kurzwort}.JPG`}
                        />
                      )}
                    </StackItemShim>
                  </StackShim>
                </div>
              </div>
              {nutImageName != "" && (
                <Image
                  width={300}
                  src={`${baseUrl}/IMAGES/CONFIST/MASS/${nutImageName}.PNG`}
                />
              )}
            </div>
          </>
        )}

        {[
          "Standard_FL_G",
          "Inline_FL_G",
          "Standard_FL_O",
          "Inline_FL_O",
        ].includes(props.gearbox.key) && (
          <div style={{ marginTop: 20, marginBottom: 10 }}>
            <h6>{i18n.t<string>("protectiveCoating")}</h6>
            <Checkbox
              checked={specification.withProtectiveCoating}
              onChange={(ev, data) => {
                handleSpecificationValuesChange(
                  "withProtectiveCoating",
                  data.checked ? data.checked : false,
                );
              }}
              label={i18n.t<string>("withProtectiveCoating")}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default GearedOffsetHeadSpecifications;
